import { SeatCard } from '@/components/admin/seat-card';
import type { ModelCatalog } from '@/lib/model-catalog';
import { PLAYER_LABEL, PLAYER_PROBLEM_SENTENCE, playerProblem } from '@/lib/replay-settings';
import { cn } from '@/lib/utils';
import type { ModelSeat, ModelTariff } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * « Modèle du joueur » (docs/specs/npc.md §13.4, N7, H28, issue #210): the model a replay the
 * SERVER plays runs on by default, over the three texts of Réglages > Rejeu.
 *
 * ## The fields of a seat, by the component of a seat
 *
 * The door, the model behind it, its tariff, the free entry one link away: the card of Réglages >
 * Modèles (`SeatCard`), handed a bare face - no title, no frame, the block's own. The player is not
 * a seat of the device (§7.1): it has no place in the cascade of a session, no session type to
 * estimate a cost on, and no « Au démarrage du dispositif » line - the default of Réglages > Rejeu is
 * the one in force and nothing else. It is not versioned either: it is saved WITH the texts,
 * by « Enregistrer et journaliser », and written to the journal beside them.
 *
 * ## Said once, where it is read
 *
 * The line under the title says the three things an operator cannot deduce from a model: that it is
 * the default of a replay the server plays, that a launch can replace it for itself alone, and that
 * Claude, playing through the assistant, ignores it.
 *
 * The block wears the yellow of the planche when the draft differs from the model in force, the same
 * signal as a touched text, and the refusal of an empty model is read under it.
 */

export interface ReplayPlayerBlockProps {
  /** What the operator is holding, not yet written. */
  readonly value: ModelSeat;
  /** The model in force. */
  readonly stored: ModelSeat;
  /** The draft differs from the one in force. */
  readonly touched: boolean;
  readonly disabled: boolean;
  /** The tiles of this deploy (#175): the providers' lists, or the built-in ones. */
  readonly catalog: ModelCatalog;
  /** The published rates this deploy knows, to prefill a seat that moves. */
  readonly tariffs: readonly ModelTariff[];
  /** The sentence that says where the tiles come from, under the card. */
  readonly catalogSource: string;
  readonly onChange: (next: ModelSeat) => void;
}

export function ReplayPlayerBlock({
  value,
  stored,
  touched,
  disabled,
  catalog,
  tariffs,
  catalogSource,
  onChange,
}: ReplayPlayerBlockProps) {
  const titleId = useId();
  const problem = playerProblem(value);

  return (
    <section
      aria-labelledby={titleId}
      data-testid="rejeu-joueur"
      data-touched={touched ? 'true' : undefined}
      className={cn(
        'flex flex-col gap-4 rounded-lg border-2 px-5 py-4',
        touched ? 'border-jaune-deep bg-jaune-tint/30' : 'border-gray-200',
      )}
    >
      <div className="flex flex-col gap-1.5">
        <h3 id={titleId} className="font-bold text-bleu-nuit text-h4">
          {PLAYER_LABEL}
        </h3>
        <p className="max-w-[96ch] text-gray-600 text-small leading-[21px]">
          Le modèle par défaut d’un rejeu joué par le serveur. Remplaçable au lancement, pour ce
          lancement seulement ; chaque rejeu garde le modèle qui l’a joué. Un rejeu joué par Claude
          n’en tient pas compte. Le changer s’inscrit dans « Ce qui a déjà bougé », comme les trois
          textes.
        </p>
      </div>

      {/* `inert` while a save is in flight: nothing is typable under the write, as the texts are. */}
      <div {...(disabled ? { inert: true } : {})}>
        <SeatCard
          id="player"
          group={null}
          title={null}
          // The section names the block « Modèle du joueur »; the card inside it is the seat.
          label="Siège du joueur"
          note={null}
          value={value}
          committed={null}
          stored={stored}
          catalog={catalog}
          tariffs={tariffs}
          tilesSeat="interviewer"
          estimateSeat={null}
          modelLabel="Modèle du joueur (identifiant)"
          providerLabel="Hébergeur du joueur"
          subgrid={false}
          onModel={(model) => onChange({ ...value, model })}
          onProvider={(provider) => onChange({ ...value, provider })}
          onTariff={(field, price) =>
            onChange({ ...value, pricing: { ...value.pricing, [field]: price } })
          }
          onSeat={onChange}
        />
      </div>

      {problem === null ? null : (
        <p role="alert" data-testid="rejeu-refus-joueur" className="text-rouge-deep text-small">
          {PLAYER_PROBLEM_SENTENCE}
        </p>
      )}

      <p data-testid="rejeu-joueur-catalogue" className="text-caption text-gray-600">
        {catalogSource} La saisie à la main reste ouverte.
      </p>
    </section>
  );
}
